import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import { ClienteService } from '../../../../services/ClienteService';
import ClienteDatosContactos from './DatosContactos';

const PRECIO_TIPOS = [
  { value: '1', label: 'Consolidado' },
  { value: '2', label: 'Porcentaje' },
  { value: '3', label: 'Devolución' },
];

export const ClienteDatosGenerales = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;

  const [estados, setEstados] = useState([]);
  const [grupoEmbalajes, setGrupoEmbalajes] = useState([]);
  const [colonias, setColonias] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  // Cargar catálogo de opciones (estados, grupo de embalaje)
  useEffect(() => {
    let mounted = true;
    const fetchCatalogOptions = async () => {
      try {
        const opts = await ClienteService.obtenerCatalogoOptions();
        if (mounted && opts) {
          if (opts.estados) {
            setEstados(
              opts.estados.map((e) => ({
                value: String(e.id),
                label: e.estado,
              }))
            );
          }
          if (opts.grupo_embalajes) {
            setGrupoEmbalajes(
              opts.grupo_embalajes.map((g) => ({
                value: String(g.id),
                label: g.grupo,
              }))
            );
          }
        }
      } catch (err) {
        console.error('Error cargando catálogos de datos generales', err);
      }
    };
    fetchCatalogOptions();
    return () => {
      mounted = false;
    };
  }, []);

  // Carga de datos iniciales del cliente
  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const data = await ClienteService.obtenerDetalleCompleto(clientId);
      if (!data) return null;

      // Si el cliente ya tiene dirección con colonias o ciudades pre-cargadas
      if (data.direccion) {
        if (data.direccion.colonias) {
          setColonias(
            data.direccion.colonias.map((c) => ({
              value: String(c.id),
              label: c.colonia,
            }))
          );
        }
        if (data.direccion.ciudades) {
          setCiudades(
            data.direccion.ciudades.map((c) => ({
              value: String(c.id),
              label: c.ciudad,
            }))
          );
        }
      } else if (data.cliente_cp && String(data.cliente_cp).length === 5) {
        // Carga diferida de colonias si no vinieron en direccion
        const dirRes = await ClienteService.obtenerFormGenerales({ cp: String(data.cliente_cp) });
        if (dirRes?.direccion?.colonias) {
          setColonias(
            dirRes.direccion.colonias.map((c) => ({
              value: String(c.id),
              label: c.colonia,
            }))
          );
        }
        if (dirRes?.direccion?.ciudades) {
          setCiudades(
            dirRes.direccion.ciudades.map((c) => ({
              value: String(c.id),
              label: c.ciudad,
            }))
          );
        }
      }

      return {
        cliente_cp: data.cliente_cp || '',
        cliente_id_colonia: data.cliente_id_colonia ? String(data.cliente_id_colonia) : '',
        cliente_domicilio: data.cliente_domicilio || '',
        cliente_next: data.cliente_next || '',
        cliente_nint: data.cliente_nint || '',
        cliente_telefono: data.cliente_telefono || '',
        cliente_id_estado: data.cliente_id_estado ? String(data.cliente_id_estado) : '',
        cliente_id_ciudad: data.cliente_id_ciudad ? String(data.cliente_id_ciudad) : '',
        cliente_id_grupo_embalaje: data.cliente_id_grupo_embalaje ? String(data.cliente_id_grupo_embalaje) : '1',
        cliente_id_tipo_precio: data.cliente_id_tipo_precio ? String(data.cliente_id_tipo_precio) : '1',
        cliente_nuevo: Boolean(data.cliente_nuevo),
        cliente_fecha_alta: data.cliente_fecha_alta ? String(data.cliente_fecha_alta).substring(0, 10) : '',
      };
    } catch (e) {
      console.error('Error al cargar datos generales del cliente', e);
      return null;
    }
  }, [clientId]);

  // Manejo de cambio de Código Postal
  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    const cpStr = String(val || '').trim();
    if (cpStr.length === 5) {
      try {
        const res = await ClienteService.obtenerFormGenerales({ cp: cpStr });
        if (res?.direccion) {
          const { direccion } = res;
          if (direccion.colonias) {
            setColonias(
              direccion.colonias.map((c) => ({
                value: String(c.id),
                label: c.colonia,
              }))
            );
          }
          if (direccion.ciudades) {
            setCiudades(
              direccion.ciudades.map((c) => ({
                value: String(c.id),
                label: c.ciudad,
              }))
            );
          }
          if (direccion.estado?.id) {
            setFieldValue('cliente_id_estado', String(direccion.estado.id));
          }
          if (direccion.ciudad?.id) {
            setFieldValue('cliente_id_ciudad', String(direccion.ciudad.id));
          }
        }
      } catch (err) {
        console.error('Error al consultar CP', err);
      }
    } else {
      setColonias([]);
      setFieldValue('cliente_id_colonia', '');
    }
  }, []);

  // Manejo de cambio de Estado para actualizar ciudades
  const handleEstadoChange = useCallback(async (val, { setFieldValue }) => {
    if (val) {
      try {
        const res = await ClienteService.obtenerFormGenerales({ e: val });
        if (res?.ciudades) {
          setCiudades(
            res.ciudades.map((c) => ({
              value: String(c.id),
              label: c.ciudad,
            }))
          );
        }
      } catch (err) {
        console.error('Error al consultar ciudades por estado', err);
      }
    } else {
      setCiudades([]);
      setFieldValue('cliente_id_ciudad', '');
    }
  }, []);

  const fields = useMemo(
    () => [
      {
        name: 'section_domicilio',
        label: 'Domicilio Fiscal y Operativo',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_cp',
        label: 'Código Postal',
        type: 'number',
        col: 3,
        required: true,
        validators: [Validators.required('El código postal es requerido')],
        helperText: '5 dígitos para autocompletar',
        onChange: handleCpChange,
      },
      {
        name: 'cliente_id_colonia',
        label: 'Colonia',
        type: 'select',
        col: 5,
        required: true,
        validators: [Validators.required('La colonia es requerida')],
        options: colonias,
        placeholder: colonias.length ? 'Seleccione colonia' : 'Ingrese un CP válido',
      },
      {
        name: 'cliente_domicilio',
        label: 'Calle / Domicilio',
        type: 'text',
        col: 4,
        required: true,
        validators: [Validators.required('El domicilio es requerido')],
        placeholder: 'Calle y número o avenida',
      },
      {
        name: 'cliente_next',
        label: 'N. Exterior',
        type: 'text',
        col: 3,
        placeholder: 'Ej. 123',
      },
      {
        name: 'cliente_nint',
        label: 'N. Interior',
        type: 'text',
        col: 3,
        placeholder: 'Ej. Piso 4, Int B',
      },
      {
        name: 'cliente_telefono',
        label: 'Teléfono',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('El teléfono es requerido')],
        placeholder: '10 dígitos',
      },
      {
        name: 'cliente_id_estado',
        label: 'Estado',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El estado es requerido')],
        options: estados,
        onChange: handleEstadoChange,
      },
      {
        name: 'cliente_id_ciudad',
        label: 'Delegación o Municipio',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El municipio es requerido')],
        options: ciudades,
        placeholder: ciudades.length ? 'Seleccione municipio' : 'Seleccione un estado',
      },
      {
        name: 'section_operativo',
        label: 'Condiciones Operativas',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_id_grupo_embalaje',
        label: 'Grupo de Embalaje',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El grupo de embalaje es requerido')],
        options: grupoEmbalajes,
      },
      {
        name: 'cliente_id_tipo_precio',
        label: 'Tipo de Precio',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El tipo de precio es requerido')],
        options: PRECIO_TIPOS,
      },
      {
        name: 'cliente_nuevo',
        label: 'Cliente Nuevo',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_fecha_alta',
        label: 'Fecha de Alta',
        type: 'date',
        col: 6,
      },
    ],
    [estados, grupoEmbalajes, colonias, ciudades, handleCpChange, handleEstadoChange]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        id: clientId,
        ...values,
      };
      const res = await ClienteService.guardar(payload);
      return res;
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      ClienteService.invalidarDetalleCompleto(clientId);
      if (onSave) {
        onSave(response);
      }
    },
    [clientId, onSave]
  );

  return (
    <div className="space-y-4">
      <CatalogForm
        title="Datos Generales"
        subtitle="Domicilio y condiciones operativas asociadas a este cliente"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        submitButtonText="Guardar Datos Generales"
      />
      <div className="mt-4">
        <ClienteDatosContactos clienteId={clientId} />
      </div>
    </div>
  );
};

export default withRouter(ClienteDatosGenerales);
